
import { Injectable } from '@angular/core';
import { Device, DeviceInfo } from '@capacitor/device';
import { App } from '@capacitor/app';
import { GlobalConstants } from '../constant/constant';
import { environment } from 'src/environments/environment';
import { Network } from '@capacitor/network';
import { DataService } from './data.service';
import { Platform } from '@ionic/angular';

@Injectable({
  providedIn: 'root',
})
export class DeviceInfoService extends DataService {
  private deviceInfo: DeviceInfo;
  constructor(private platform: Platform) {
    super();
  }

  async getPlatform(): Promise<"ios" | "android" | "web"> {
    return Device.getInfo().then(deviceInfo => deviceInfo.platform);
  }

  async getOS(): Promise<string> {
    return Device.getInfo().then(deviceInfo => deviceInfo.operatingSystem);
  }

  async getOSMajorVersion(): Promise<number> {
    return Device.getInfo().then(deviceInfo => +deviceInfo.osVersion.split('.')[0]);
  }

  getBrowserName(): string {
    const agent = window.navigator.userAgent.toLowerCase();
    switch (true) {
      case agent.indexOf('edge') > -1:
        return 'edge';
      case agent.indexOf('chrome') > -1 && !!(<any>window).chrome:
        return 'chrome';
      case agent.indexOf('firefox') > -1:
        return 'firefox';
      case agent.indexOf('safari') > -1:
        return 'safari';
      default:
        return 'other';
    }
  }

  async getAppVersion(): Promise<string> {
    let platform = await this.getPlatform();
    if (platform !== 'web') {
      const appInfo = await App.getInfo();
      return appInfo.version;
    } else {
      return environment.version;
    }
  }

  async getInputType(): Promise<string> {
    let versionNumber = await this.getAppVersion();
    let platform = await this.getPlatform();
    if (platform === 'web') {
      let browserName = this.getBrowserName();
      return GlobalConstants.INPUT_TYPE_PATTERN(browserName, versionNumber);
    } else {
      return GlobalConstants.INPUT_TYPE_PATTERN(platform, versionNumber);
    }
  }

  /**
    * Checks if the current device is a mobile device.
    *
    * Returns true if:
    *  - The platform is not desktop AND
    *  - The platform is iOS or Android OR
    *  - The user agent matches common mobile devices and the platform is not a tablet
    *
    * @returns true if running on a mobile device (native or mobile browser), false otherwise
    */
  checkIsMobile(): boolean {
    const p = this.platform;
    const platforms = p.platforms();

    const isMobile = Boolean(
      /Android|webOS|iPhone|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
      !platforms.includes('tablet')
    );

    return !p.is('desktop') && (p.is('ios') || p.is('android')  || isMobile
    );
  }

  checkIsMobileWeb(): boolean {
    return this.platform.is("mobileweb");
  }

  async getNetworkStatus() {
    const status = await Network.getStatus();
    return status;
  }

  async getDeviceId(): Promise<string> {
    const response = await this.http.getOnlyBody<{deviceId: string}>(GlobalConstants.DEVICE_IDS_URI).toPromise();
    return response.deviceId;
  }

  async openAppStore() {
    if (this.platform.is('ios')) {
      window.open(GlobalConstants.STORE_URLS.IOS, '_blank');
    } else if (this.platform.is('android')) {
      window.open(GlobalConstants.STORE_URLS.ANDROID, '_blank');
    }
  }
}
